import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

export function mediaUrl(urlInput) {
  if (!urlInput) return "";
  let url = typeof urlInput === "string" ? urlInput : urlInput?.url || urlInput?.src || "";
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("blob:") || url.startsWith("data:")) {
    return url;
  }
  const base = BACKEND_URL || "";
  if (url.startsWith("/api/")) {
    return `${base}${url}`;
  }
  if (url.startsWith("/uploads/")) {
    return `${base}/api${url}`;
  }
  if (url.startsWith("uploads/")) {
    return `${base}/api/${url}`;
  }
  if (url.startsWith("/")) {
    return `${base}${url}`;
  }
  return `${base}/${url}`;
}

export function optimizedImageUrl(urlInput, width = 450) {
  const full = mediaUrl(urlInput);
  if (!full) return "";
  
  // 1. Optimize Pexels Images
  if (full.includes("images.pexels.com")) {
    try {
      const u = new URL(full);
      u.searchParams.set("auto", "compress");
      u.searchParams.set("cs", "tinysrgb");
      u.searchParams.set("w", String(width));
      return u.toString();
    } catch {
      return full.replace(/(\?|&)w=\d+/g, "").concat(`?auto=compress&cs=tinysrgb&w=${width}`);
    }
  }

  // 2. Optimize Unsplash Images
  if (full.includes("images.unsplash.com")) {
    try {
      const u = new URL(full);
      u.searchParams.set("auto", "format");
      u.searchParams.set("fit", "crop");
      u.searchParams.set("w", String(width));
      u.searchParams.set("q", "80");
      return u.toString();
    } catch {
      return full;
    }
  }

  // 3. Optimize Cloudinary Images
  if (full.includes("res.cloudinary.com") && full.includes("/upload/")) {
    return full.replace("/upload/", `/upload/f_auto,q_auto,w_${width}/`);
  }

  return full;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function errMsg(e) {
  return formatApiErrorDetail(e?.response?.data?.detail) || e?.response?.data?.message || (e?.code === "ERR_NETWORK" ? "Unable to connect to server. Please ensure backend is running." : e?.message) || "Something went wrong. Please try again.";
}

export function waLink(number, text = "") {
  let n = (number || "").toString().replace(/[^0-9]/g, "");
  if (!n) {
    n = "919082444611";
  } else if (n.length === 10) {
    n = `91${n}`;
  }
  return `https://wa.me/${n}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
}

export async function downloadImage(url, filename = "design-reference.jpg") {
  if (!url) return;

  let safeFilename = filename;
  const extMatch = url.match(/\.(jpg|jpeg|png|webp|svg|gif)($|\?)/i);
  const ext = extMatch ? extMatch[1] : "jpg";
  if (!safeFilename.includes(".")) {
    safeFilename = `${safeFilename}.${ext}`;
  }

  let endpointUrl = "";
  if (url.includes("/api/uploads/")) {
    const filePart = url.split("/api/uploads/").pop().split("?")[0];
    endpointUrl = `${API}/download?file=${encodeURIComponent(filePart)}&filename=${encodeURIComponent(safeFilename)}`;
  }

  const fullUrl = endpointUrl || mediaUrl(url);

  try {
    const res = await fetch(fullUrl);
    if (!res.ok) throw new Error("Fetch failed");
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = safeFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000);
    return;
  } catch (err) {
    console.warn("Direct blob download failed, trying anchor fallback:", err);
  }

  try {
    const a = document.createElement("a");
    a.href = endpointUrl || fullUrl;
    a.download = safeFilename;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (err2) {
    window.open(endpointUrl || fullUrl, "_blank");
  }
}

export default api;
